import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

// Revisión de contratos con IA (spec 012). Sólo tokens de Fluent v9 (sin hex ni style={{}}) para que funcione en claro,
// oscuro y alto contraste; el estado nunca se comunica sólo con color (StatusPill con texto y símbolo en alto contraste).

export const useContractReviewStyles = makeStyles({
  // Historial dentro del drawer: lista con divisores de la guía (variables --ichtys-*, también en alto contraste).
  historyList: {
    listStyleType: 'none',
    ...shorthands.margin('0'),
    ...shorthands.padding('0'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', 'var(--ichtys-border)'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium)
  },
  historyItem: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ':not(:last-child)': {
      ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', 'var(--ichtys-border)')
    }
  },
  secondary: {
    color: 'var(--ichtys-text-secondary)',
    fontSize: '13px'
  },
  error: {
    color: 'var(--ichtys-error)',
    fontSize: '13px'
  },
  checkList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS
  },
  scopeOption: {
    display: 'flex',
    flexDirection: 'column'
  },
  scopeDescription: {
    color: 'var(--ichtys-text-secondary)',
    fontSize: '13px',
    paddingLeft: '32px',
    marginTop: '-4px'
  },
  // Contador de una sección del drawer (mismo criterio que el drawer de importación).
  count: {
    flexShrink: 0,
    minWidth: '20px',
    ...shorthands.padding('1px', '7px'),
    ...shorthands.borderRadius('999px'),
    backgroundColor: 'var(--ichtys-row-selected-bg)',
    ...shorthands.border('1px', 'solid', 'var(--ichtys-pill-info-border)'),
    fontSize: '11px',
    fontWeight: tokens.fontWeightBold,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums'
  },
  launchActions: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS
  },

  // Bloques
  /** Cuerpo de la página: con márgenes y nunca más ancho que el contenedor (.content oculta el desborde). */
  page: {
    boxSizing: 'border-box',
    maxWidth: '100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXXL, tokens.spacingVerticalXXL),
    '@media (max-width: 768px)': {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL, tokens.spacingVerticalXL)
    }
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    // Un hijo flex no se achica por debajo de su contenido salvo que se lo permita: sin esto, una tabla
    // o un bloque de código largo empujan el ancho de toda la página.
    minWidth: 0
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalS
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    rowGap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1
  },
  cardSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1)
  },
  muted: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200
  },
  label: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  numbers: {
    fontVariantNumeric: 'tabular-nums'
  },
  quote: {
    ...shorthands.margin('0'),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    ...shorthands.borderLeft(tokens.strokeWidthThick, 'solid', tokens.colorNeutralStroke1),
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic'
  },
  code: {
    ...shorthands.margin('0'),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground3,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    maxWidth: '100%',
    overflowX: 'auto'
  },
  comparison: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalS,
    '& > *': { minWidth: 0 }
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    '& > *': { minWidth: 0 }
  },
  /** Contenedor de tabla: si en pantallas angostas no entra, se desplaza ella sola y no la página. */
  tableWrap: {
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      textAlign: 'left',
      color: tokens.colorNeutralForeground2,
      fontWeight: tokens.fontWeightSemibold,
      fontSize: tokens.fontSizeBase200,
      ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
      ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
      overflowWrap: 'anywhere'
    },
    '& td': {
      ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
      ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke3),
      verticalAlign: 'top',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word'
    }
  },
  focusable: {
    ':focus-visible': {
      ...shorthands.outline(tokens.strokeWidthThick, 'solid', tokens.colorStrokeFocus2)
    }
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.overflow('hidden'),
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap'
  }
});
